import { THUMBNAIL_JPEG_QUALITY, THUMBNAIL_MIMETYPE } from "./imageThumbnail";

export interface VideoThumbnail {
    blob: Blob;
    width: number;
    height: number;
    videoWidth: number;
    videoHeight: number;
    durationMs: number;
}

export const VIDEO_THUMBNAIL_MAX_EDGE_PX = 800;
const VIDEO_THUMBNAIL_MIN_SEEK_SECONDS = 0.1;
const VIDEO_THUMBNAIL_MAX_SEEK_SECONDS = 1;

function waitForEvent(video: HTMLVideoElement, eventName: 'loadedmetadata' | 'seeked', signal?: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        const cleanup = () => {
            video.removeEventListener(eventName, onDone);
            video.removeEventListener('error', onError);
            signal?.removeEventListener('abort', onAbort);
        };
        const onDone = () => {
            cleanup();
            resolve();
        };
        const onError = () => {
            cleanup();
            reject(new Error('Failed to decode video'));
        };
        const onAbort = () => {
            cleanup();
            reject(new Error('Video thumbnail aborted'));
        };
        if (signal?.aborted) {
            reject(new Error('Video thumbnail aborted'));
            return;
        }
        video.addEventListener(eventName, onDone);
        video.addEventListener('error', onError);
        signal?.addEventListener('abort', onAbort, { once: true });
    });
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
    return new Promise(resolve => {
        canvas.toBlob(resolve, THUMBNAIL_MIMETYPE, THUMBNAIL_JPEG_QUALITY);
    });
}

export function getVideoSeekTime(durationSeconds: number): number {
    if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) return VIDEO_THUMBNAIL_MIN_SEEK_SECONDS;
    return Math.min(
        Math.max(durationSeconds * 0.1, VIDEO_THUMBNAIL_MIN_SEEK_SECONDS),
        VIDEO_THUMBNAIL_MAX_SEEK_SECONDS,
        durationSeconds,
    );
}

export async function generateVideoThumbnail(file: File, signal?: AbortSignal): Promise<VideoThumbnail | null> {
    if (!file.type.startsWith('video/')) return null;

    const objectUrl = URL.createObjectURL(file);
    const video = document.createElement('video');

    try {
        video.muted = true;
        video.playsInline = true;
        video.preload = 'metadata';
        const metadataLoaded = waitForEvent(video, 'loadedmetadata', signal);
        video.src = objectUrl;
        await metadataLoaded;

        const sourceWidth = video.videoWidth;
        const sourceHeight = video.videoHeight;
        if (!sourceWidth || !sourceHeight) return null;

        const seeked = waitForEvent(video, 'seeked', signal);
        video.currentTime = getVideoSeekTime(video.duration);
        await seeked;

        const scale = Math.min(1, VIDEO_THUMBNAIL_MAX_EDGE_PX / Math.max(sourceWidth, sourceHeight));
        const width = Math.max(1, Math.round(sourceWidth * scale));
        const height = Math.max(1, Math.round(sourceHeight * scale));

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(video, 0, 0, width, height);

        const blob = await canvasToBlob(canvas);
        if (!blob) return null;

        return {
            blob,
            width,
            height,
            videoWidth: sourceWidth,
            videoHeight: sourceHeight,
            durationMs: Number.isFinite(video.duration) ? Math.round(video.duration * 1000) : 0,
        };
    } catch {
        return null;
    } finally {
        video.removeAttribute('src');
        video.load();
        URL.revokeObjectURL(objectUrl);
    }
}
